import axiosInstance from 'integration/api/axiosInstance';
import {
  ProfessionalCreationDto,
  ProfessionalSetPasswordDto,
  ProfessionalUpdateDto,
  CommonEntitiesListResponse,
  ProfessionalResponseDto,
  PatientBriefResponseDto,
  SpokenLanguageDto,
  PatientPurchasedSubscriptionForProfessionalResponseDto,
  CpSubscriptionResponseDto,
  CpSubscriptionStatsResponseDto,
  ProfessionalRestoreRequestDto,
  ProfessionalUpdateResponseDto,
} from '@docbay/schemas';
import { fetchProfessionalsProps } from 'common/types/professionals';
import { getLanguageFromLocalStorage } from 'applicaiton/sessionStorage/language';
import axiosAuthInstance from './axiosAuthInstance';
import { ProfessionalsSubscribersParams } from 'applicaiton/store/reducers/Professionals/models';

/**
 * Professionals API methods.
 */
export const ProfessionalsAPI = {
  /**
   * Fetches a list of professionals based on the given search parameters.
   * @param props - The search parameters.
   * @returns A promise that resolves to a CommonEntitiesListResponse object containing the list of professionals.
   */
  fetchProfessionals: ({
    searchItem,
    page,
    limit,
    clinicIds,
    invertedChain,
    onlyActive,
  }: fetchProfessionalsProps) => {
    const params = {
      includeDeleted: 'false',
      include:
        'user,photos,mainPhoto,clinicsRelations.specializations.specialization,clinicsRelations.clinic,professionalSpokenLanguages,socialMedia',
      ...(page ? { page } : {}),
      ...(limit ? { limit } : {}),
      ...(searchItem ? { search: searchItem } : {}),
      ...(clinicIds?.length ? { clinicIds } : {}),
      ...(invertedChain ? { invertedChain } : {}),
      ...(onlyActive ? { onlyActive } : {}),
    };

    return axiosInstance.get<
      CommonEntitiesListResponse<ProfessionalResponseDto>
    >('/professionals', {
      params,
    });
  },

  /**
   * Fetches a professional by its ID.
   * @param id - The ID of the professional to fetch.
   * @returns A promise that resolves to a ProfessionalResponseDto object containing the fetched professional's data.
   */
  fetchProfessionalById: (id: string) => {
    const params = {
      include:
        'clinicsRelations,clinicsRelations.clinic,clinicsRelations.schedule,clinicsRelations.specializations.specialization,mainPhoto,photos,user,socialMedia,professionalSpokenLanguages,notificationConfig,clinicsRelations.clinic.owner,clinicsRelations.subscriptions',
    };
    return axiosInstance.get<ProfessionalResponseDto>(`/professionals/${id}`, {
      params,
    });
  },

  /**
   * Removes a professional by its ID.
   * @param id - The ID of the professional to remove.
   * @returns A promise that resolves when the professional is successfully removed.
   */
  removeProfessional: (id: number) =>
    axiosInstance.delete(`/professionals/${id}`),

  /**
   * Checks if the given verification code for a professional's email is valid.
   * @param email - The email of the professional to check the verification code for.
   * @param code - The verification code to check.
   * @returns A promise that resolves when the verification code is successfully checked.
   */
  checkProfessionalVerificationCode: (email: string, code: string) =>
    axiosAuthInstance('').post('/professionals/verification-code', {
      email,
      code,
    }),

  /**
   * Creates a password for a professional.
   * @param data - The ProfessionalSetPasswordDto object containing the necessary information to create the password.
   * @returns A promise that resolves when the password is successfully created.
   */
  createProfessionalPassword: (data: ProfessionalSetPasswordDto) =>
    axiosAuthInstance('').post('/professionals/password', data),

  /**
   * Creates a new professional.
   * @param data - The ProfessionalCreationDto object containing the necessary information to create the professional.
   * @returns A promise that resolves when the professional is successfully created.
   */
  createProfessional: (data: ProfessionalCreationDto) => {
    const language = getLanguageFromLocalStorage();
    return axiosInstance.post('/professionals/sign-up', { ...data, language });
  },

  /**
   * Updates an existing professional's data.
   * @param id - The ID of the professional to update.
   * @param data - The ProfessionalUpdateDto object containing the updated information for the professional.
   * @returns A promise that resolves when the professional is successfully updated.
   */
  updateProfessional: (id: string, data: Partial<ProfessionalUpdateDto>) =>
    axiosInstance.patch<ProfessionalUpdateResponseDto>(
      `/professionals/${id}`,
      data,
    ),

  /**
   * Updates an existing professional's email address.
   * @param id - The ID of the professional to update.
   * @param email - The new email address for the professional.
   * @returns A promise that resolves when the email is successfully updated.
   */
  updateProfessionalEmail: (id: string, email: string) => {
    const language = getLanguageFromLocalStorage();
    return axiosInstance.patch<ProfessionalResponseDto>(
      `/professionals/${id}/email`,
      {
        email,
        language,
      },
    );
  },

  /**
   * Resends the authorization data for a professional.
   * @param id - The ID of the professional to resend the authorization data for.
   * @returns A promise that resolves when the authorization data is successfully resent.
   */
  resendAuthorizationData: (id: string) => {
    const language = getLanguageFromLocalStorage();
    return axiosInstance.post(
      `/professionals/${id}/resend-authorization-data`,
      { language },
    );
  },

  /**
   * Fetches a list of spoken languages for professionals.
   * @returns A promise that resolves to a CommonEntitiesListResponse object containing the list of spoken languages.
   */
  fetchProfessionalsSpokenLanguages: () => {
    return axiosInstance.get<CommonEntitiesListResponse<SpokenLanguageDto>>(
      '/professionals/spoken-languages',
    );
  },

  /**
   * Creates a new professional on behalf of a clinic owner.
   * @param data - The ProfessionalCreationDto object containing the necessary information to create the professional.
   * @returns A promise that resolves when the professional is successfully created.
   */
  createClinicOwnerProfessional: (data: ProfessionalCreationDto) => {
    return axiosInstance.post<ProfessionalResponseDto>(
      '/professionals/create-by-clinic-owner',
      data,
    );
  },

  /**
   * Restores a clinic owner's professional using the given parameters and token.
   * @param params - The ProfessionalRestoreRequestDto object containing the necessary information to restore the professional.
   * @param token - The authentication token for the API request.
   * @returns A promise that resolves when the professional is successfully restored.
   */
  restoreClinicOwnerProfessional: (
    params: ProfessionalRestoreRequestDto,
    token: string,
  ) => {
    return axiosAuthInstance(token).post('/professionals/restore', {
      email: params.email,
      ...(params.clinicIds ? { clinicIds: params.clinicIds } : {}),
    });
  },

  /**
   * Fetches a list of purchased subscriptions for a professional.
   * @param id - The ID of the professional to fetch the subscriptions for.
   * @param params - The ProfessionalsSubscribersParams object containing any necessary filter parameters.
   * @returns A promise that resolves to a CommonEntitiesListResponse object containing the list of purchased subscriptions.
   */
  fetchPurchasedSubscription: (
    id: string,
    params: ProfessionalsSubscribersParams,
  ) => {
    return axiosInstance.get<
      CommonEntitiesListResponse<PatientPurchasedSubscriptionForProfessionalResponseDto>
    >(`/professionals/${id}/patients-purchased-subscriptions`, { params });
  },

  /**
   * Fetches a list of subscribed patients for the current professional.
   * @param search - An optional search query to filter the results by.
   * @returns A promise that resolves to an array of PatientBriefResponseDto objects containing the list of subscribed patients.
   */
  fetchSubscribedPatients: (search: string) => {
    return axiosInstance.get<PatientBriefResponseDto[]>(
      '/professionals/subscribed-patients',
      {
        params: {
          ...(search ? { search } : {}),
        },
      },
    );
  },

  /**
   * Fetches a list of subscriptions for the current professional.
   * @param params - An optional object containing any necessary filter parameters.
   * @returns A promise that resolves to an array of CpSubscriptionResponseDto objects containing the list of subscriptions.
   */
  fetchSubscriptions: (params: { clinicIds?: string[] }) => {
    return axiosInstance.get<CpSubscriptionResponseDto[]>(
      '/professionals/subscriptions',
      { params },
    );
  },

  /**
   * Fetches statistics for a set of subscriptions.
   * @param params - An optional object containing any necessary filter parameters.
   * @returns A promise that resolves to a CpSubscriptionStatsResponseDto object containing the subscription statistics.
   */
  fetchSubscriptionStatistic: (params: { packageIds?: string[] }) => {
    return axiosInstance.get<CpSubscriptionStatsResponseDto>(
      '/professionals/subscriptions/statistics',
      { params },
    );
  },
};
